import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, type FeatureFlagRow } from '../lib/api'
import { Empty, ErrorNote, Toggle } from '../components/Page'
import { Card, CardNote, ConsoleHead } from '../components/admin/Console'

/**
 * Feature switches.
 *
 * Every flag with `is_ai_feature` set is grouped and marked, so an institution
 * can turn off everything model-generated in one action rather than hunting
 * for it. The marker is a dashed accent outline and the letters AI: a model's
 * output is never left to be inferred from context.
 */
export function FeatureFlagsPage() {
  const queryClient = useQueryClient()

  const flags = useQuery({
    queryKey: ['admin', 'flags'],
    queryFn: () => api.get<FeatureFlagRow[]>('/admin/feature-flags'),
  })

  const update = useMutation({
    mutationFn: (input: { id: string; enabled: boolean }) =>
      api.patch<FeatureFlagRow>(`/admin/feature-flags/${input.id}`, { enabled: input.enabled }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'flags'] })
      void queryClient.invalidateQueries({ queryKey: ['me'] })
      void queryClient.invalidateQueries({ queryKey: ['config'] })
    },
  })

  const rows = flags.data ?? []
  // AI first, as the board groups them: the tinted band is the thing an
  // institution's privacy officer is looking for.
  const ordered = [...rows].sort((a, b) => {
    if (a.is_ai_feature !== b.is_ai_feature) return a.is_ai_feature ? -1 : 1
    return a.key.localeCompare(b.key)
  })
  const aiOn = rows.filter((flag) => flag.is_ai_feature && flag.enabled)

  return (
    <>
      <ConsoleHead
        title="Feature switches"
        note={`${rows.filter((flag) => flag.enabled).length} on of ${rows.length}`}
        actions={
          <button
            type="button"
            className="btn-secondary"
            disabled={!aiOn.length || update.isPending}
            onClick={() => {
              for (const flag of aiOn) update.mutate({ id: flag.id, enabled: false })
            }}
          >
            Turn off everything model-generated
          </button>
        }
      />

      <ErrorNote error={update.error} />

      <Card className="max-w-[720px]">
        {flags.isLoading ? (
          <Empty>Reading the flags.</Empty>
        ) : !ordered.length ? (
          <Empty>No flags are registered. The bootstrap inserts the catalogue on start.</Empty>
        ) : (
          ordered.map((flag, index) => (
            <div
              key={flag.id}
              className="flex items-center gap-3 px-[15px] py-[11px]"
              style={{
                borderBottom:
                  index < ordered.length - 1 ? '1px solid var(--color-neutral-200)' : 'none',
                background: flag.is_ai_feature ? 'var(--jl-accent-100)' : 'transparent',
              }}
            >
              <Toggle
                on={flag.enabled}
                disabled={update.isPending}
                label={flag.name}
                onChange={(next) => update.mutate({ id: flag.id, enabled: next })}
              />
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <div className="font-mono text-[12.5px]">{flag.key}</div>
                  {flag.is_ai_feature && (
                    <span
                      className="font-mono text-[8.5px] uppercase leading-[14px] tracking-[0.1em] text-accent"
                      style={{ border: '1px dashed var(--jl-accent)', padding: '0 4px' }}
                      title="Model-generated. Every value it produces carries a provenance marker."
                    >
                      AI
                    </span>
                  )}
                </div>
                <div className="text-[13.5px] leading-[1.35] text-ink2">
                  {flag.description ?? flag.name}
                </div>
              </div>
              <div className="flex-none font-mono text-[10px] text-ink2">
                {flag.enabled ? 'ON' : 'OFF'}
              </div>
            </div>
          ))
        )}
      </Card>

      <CardNote>
        EVERY FLAG WITH IS_AI_FEATURE SET IS GROUPED AND MARKED · AN INSTITUTION
        <br />
        CAN TURN OFF EVERYTHING MODEL-GENERATED IN ONE ACTION.
      </CardNote>

      <p className="mt-4 max-w-[70ch] text-[15px] leading-normal text-ink2">
        The correction pass writes to a separate field and never overwrites the raw text. Raw OCR is
        the authoritative record of what the paper printed; corrected text is a reading aid, labelled
        as such wherever it is shown.
      </p>
    </>
  )
}
